import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as Tabs from '@radix-ui/react-tabs';
import { AlertTriangle, ExternalLink, FlaskConical, History, RefreshCw, Send, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import {
  MESSAGE_LIMITS,
  formatDateTime,
  formatTime,
  messages,
  type GeneratedMessage,
} from '@nesc/shared';
import { api, errorMessage, type SingleResponse } from '@/lib/api-client';
import { qk } from '@/lib/query-client';
import { useAuth } from '@/lib/auth';
import { useAutosave } from '@/lib/hooks';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Field } from '@/components/ui/field';
import { Input, Textarea } from '@/components/ui/input';
import { SkeletonRows } from '@/components/ui/skeleton';
import { StatusBadge } from '@/components/common/status-badge';
import { ConfidenceMark } from '@/components/common/evidence';
import { ConfirmDialog } from '@/components/common/feedback';
import { BottomActions, PageHeader } from '@/components/common/page';
import { cn, tabTriggerClass } from '@/lib/utils';

type MessageDetail = GeneratedMessage & {
  versions: { id: string; version: number; subject: string | null; body: string; source: string; createdAt: string }[];
};

/** S43 文面編集 */
export const MessageEditPage = () => {
  const { id = '' } = useParams();
  const queryClient = useQueryClient();
  const { hasRole, user } = useAuth();

  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [dirty, setDirty] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const [testOpen, setTestOpen] = useState(false);
  const [testTo, setTestTo] = useState('');
  const [tab, setTab] = useState<'edit' | 'rationale'>('edit');

  const { data, isLoading } = useQuery({
    queryKey: qk.message(id),
    queryFn: () => api.get<SingleResponse<MessageDetail>>(`/messages/${id}`),
  });

  useEffect(() => {
    if (data && !dirty) {
      setSubject(data.data.subject ?? '');
      setBody(data.data.body);
    }
  }, [data, dirty]);

  const save = useMutation({
    mutationFn: (payload: { subject: string; body: string }) =>
      api.patch<SingleResponse<MessageDetail>>(`/messages/${id}`, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.message(id) });
      setDirty(false);
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  // 5 秒間隔で自動保存し、保存状態を文字で示す
  const { savedAt, isSaving } = useAutosave(
    { subject, body },
    useCallback(
      async (value: { subject: string; body: string }) => {
        if (!dirty) return;
        await save.mutateAsync(value);
      },
      [dirty, save],
    ),
    { enabled: dirty && data?.data.status !== 'sent' },
  );

  const regenerate = useMutation({
    mutationFn: () => api.post<SingleResponse<MessageDetail>>(`/messages/${id}/regenerate`),
    onSuccess: (result) => {
      toast.success('再生成しました', { description: '前の内容は版として残っています' });
      setSubject(result.data.subject ?? '');
      setBody(result.data.body);
      setDirty(false);
      void queryClient.invalidateQueries({ queryKey: qk.message(id) });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const approve = useMutation({
    mutationFn: () => api.post(`/messages/${id}/approve`),
    onSuccess: () => {
      toast.success('承認しました');
      void queryClient.invalidateQueries({ queryKey: qk.message(id) });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const restore = useMutation({
    mutationFn: (versionId: string) => api.post(`/messages/${id}/restore-version`, { versionId }),
    onSuccess: () => {
      toast.success('この版に戻しました');
      setDirty(false);
      void queryClient.invalidateQueries({ queryKey: qk.message(id) });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const send = useMutation({
    mutationFn: () =>
      api.post<SingleResponse<{ result: string }>>('/sends', { generatedMessageId: id }),
    onSuccess: (result) => {
      toast.success(`送信しました（結果：${result.data.result}）`);
      void queryClient.invalidateQueries({ queryKey: qk.message(id) });
      setSendOpen(false);
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const testSend = useMutation({
    mutationFn: () =>
      api.post<SingleResponse<{ ok: boolean; simulated: boolean; message: string }>>(
        `/messages/${id}/test-send`,
        { to: testTo.trim() },
      ),
    onSuccess: (result) => {
      if (result.data.ok && !result.data.simulated) {
        toast.success(messages.message.testSent(testTo.trim()), { description: result.data.message });
      } else {
        toast.error('テスト送信できませんでした', { description: result.data.message });
      }
      setTestOpen(false);
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const openTestSend = () => {
    if (!testTo) setTestTo(user?.email ?? '');
    setTestOpen(true);
  };

  if (isLoading || !data) return <SkeletonRows rows={8} />;

  const message = data.data;
  const maxBody = message.channel === 'email' ? MESSAGE_LIMITS.emailBodyMax : MESSAGE_LIMITS.formBodyMax;
  const subjectLength = [...subject].length;
  const bodyLength = [...body].length;
  // 本文の上限は推奨値で、超えても承認・送信は止めない（件名だけは超過を許さない）
  const overLimit = subjectLength > MESSAGE_LIMITS.subjectMax;
  const isSent = message.status === 'sent' || message.status === 'sending';

  const rationalePanel = (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles aria-hidden className="size-5" />
            {messages.message.rationale}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {message.rationale?.productElements.length ? (
            <div>
              <p className="mb-1 text-note font-semibold text-foreground-muted">
                {messages.message.rationaleProduct}
              </p>
              <ul className="space-y-1 text-body">
                {message.rationale.productElements.map((item) => (
                  <li key={item}>・{item}</li>
                ))}
              </ul>
            </div>
          ) : null}

          {message.rationale?.companyFacts.length ? (
            <div>
              <p className="mb-1 text-note font-semibold text-foreground-muted">
                {messages.message.rationaleCompany}
              </p>
              <ul className="space-y-1.5 text-body">
                {message.rationale.companyFacts.map((fact, index) => (
                  <li key={index} className="flex flex-wrap items-center gap-1.5">
                    <span>
                      ・{fact.field}: {fact.value}
                    </span>
                    <ConfidenceMark level={fact.confidence} />
                    {fact.sourceUrl && (
                      <a
                        href={fact.sourceUrl}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="inline-flex items-center gap-1 text-note underline underline-offset-2"
                      >
                        出典
                        <ExternalLink aria-hidden className="size-3" />
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {/* 推測を含む場合のみ表示する */}
          {message.rationale?.cautions.length ? (
            <div className="rounded-control border-2 border-foreground bg-muted p-3">
              <p className="mb-1 flex items-center gap-1.5 text-body font-semibold">
                <AlertTriangle aria-hidden className="size-4" />
                {messages.message.assumptionsTitle}
              </p>
              <ul className="space-y-1 text-body">
                {message.rationale.cautions.map((item) => (
                  <li key={item}>・{item}</li>
                ))}
              </ul>
            </div>
          ) : null}

          {message.ngPhraseHits.length > 0 && (
            <div className="rounded-control border-2 border-foreground bg-muted p-3">
              <p className="text-body font-semibold">
                {messages.message.ngPhraseWarning(message.ngPhraseHits.join('、'))}
              </p>
            </div>
          )}

          <dl className="border-t border-border pt-3 text-note text-foreground-muted">
            <div className="flex gap-2">
              <dt>{messages.message.model}</dt>
              <dd>{message.rationale?.model ?? '—'}</dd>
            </div>
            <div className="flex gap-2">
              <dt>{messages.message.generatedAt}</dt>
              <dd className="tabular">{formatDateTime(message.createdAt)}</dd>
            </div>
            {message.rationale?.promptVersion && (
              <div className="flex gap-2">
                <dt>プロンプト版</dt>
                <dd>{message.rationale.promptVersion}</dd>
              </div>
            )}
          </dl>
        </CardContent>
      </Card>

      {message.versions.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <History aria-hidden className="size-5" />
              {messages.message.versionHistory}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="divide-y divide-border">
              {message.versions.map((version) => (
                <li key={version.id} className="flex items-center gap-2 py-2.5">
                  <span className="tabular shrink-0 text-note text-foreground-muted">
                    v{version.version}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-body">
                    {version.subject ?? '（件名なし）'}
                  </span>
                  <span className="shrink-0 text-note text-foreground-subtle">
                    {version.source === 'ai' ? 'AI' : '編集'}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => restore.mutate(version.id)}
                    disabled={restore.isPending || isSent}
                  >
                    {messages.message.restoreVersion}
                  </Button>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );

  const editor = (
    <Card>
      <CardContent className="space-y-4 pt-4 lg:pt-5">
        <Field
          label={messages.message.subject}
          required
          count={{ used: subjectLength, max: MESSAGE_LIMITS.subjectMax }}
          error={
            subjectLength > MESSAGE_LIMITS.subjectMax ? messages.message.charOver : undefined
          }
        >
          {(props) => (
            <Input
              {...props}
              value={subject}
              onChange={(e) => {
                setSubject(e.target.value);
                setDirty(true);
              }}
              disabled={isSent}
            />
          )}
        </Field>

        <Field
          label={messages.message.body}
          required
          count={{ used: bodyLength, max: maxBody }}
          hint={bodyLength > maxBody ? messages.message.bodyOverNote(maxBody) : undefined}
        >
          {(props) => (
            <Textarea
              {...props}
              rows={16}
              value={body}
              onChange={(e) => {
                setBody(e.target.value);
                setDirty(true);
              }}
              disabled={isSent}
              className="font-sans"
            />
          )}
        </Field>

        <div className="flex flex-wrap gap-2 border-t border-border pt-4">
          <Button
            variant="outline"
            onClick={() => regenerate.mutate()}
            disabled={regenerate.isPending || isSent}
          >
            <RefreshCw aria-hidden />
            {messages.message.regenerate}
          </Button>
          {hasRole('manager') && (
            <Button
              variant="ghost"
              onClick={() => {
                const name = window.prompt('テンプレート名');
                if (!name) return;
                void api.post(`/messages/${id}/save-as-template`, { name }).then(() => {
                  toast.success('テンプレートとして保存しました');
                });
              }}
              disabled={isSent}
            >
              {messages.message.saveAsTemplate}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );

  return (
    <>
      <PageHeader
        back={message.campaignId ? `/campaigns/${message.campaignId}` : `/companies/${message.companyId}`}
        title={
          <>
            文面:{' '}
            <Link
              to={`/companies/${message.companyId}`}
              className="underline decoration-border-strong underline-offset-4 hover:decoration-foreground"
              title="企業詳細を開く"
            >
              {message.companyName}
            </Link>
          </>
        }
        badge={<StatusBadge kind="message_status" value={message.status} size="lg" />}
        description={
          <div className="space-y-0.5">
            <p className="text-body">
              {message.destination ? (
                <>
                  宛先: <span className="break-all font-semibold">{message.destination.value}</span>
                  {message.destination.type === 'form' && '（フォーム）'}
                </>
              ) : (
                <span className="font-semibold">有効な宛先がありません</span>
              )}
            </p>
            <p className="text-note">
              {isSaving
                ? '保存しています…'
                : savedAt
                  ? messages.message.autosaved(formatTime(savedAt))
                  : dirty
                    ? '未保存の変更があります'
                    : `v${message.version}`}
            </p>
          </div>
        }
        actions={
          <>
            {hasRole('manager') && message.status === 'draft' && (
              <Button
                variant="outline"
                onClick={() => approve.mutate()}
                disabled={approve.isPending || overLimit || message.ngPhraseHits.length > 0}
                disabledReason={
                  overLimit
                    ? messages.message.charOver
                    : message.ngPhraseHits.length > 0
                      ? messages.message.ngPhraseWarning(message.ngPhraseHits.join('、'))
                      : undefined
                }
              >
                {messages.message.approve}
              </Button>
            )}
            <Button variant="outline" onClick={openTestSend}>
              <FlaskConical aria-hidden />
              {messages.message.testSend}
            </Button>
            <Button
              onClick={() => setSendOpen(true)}
              disabled={isSent || overLimit}
              disabledReason={isSent ? 'すでに送信済みです' : overLimit ? messages.message.charOver : undefined}
            >
              <Send aria-hidden />
              {messages.message.send}
            </Button>
          </>
        }
      />

      {/* PC：本文と根拠を並べる */}
      <div className="hidden gap-4 lg:grid lg:grid-cols-[1fr_380px]">
        {editor}
        {rationalePanel}
      </div>

      {/* スマホ：タブで切り替える */}
      <div className="lg:hidden">
        <Tabs.Root value={tab} onValueChange={(v) => setTab(v as typeof tab)}>
          <Tabs.List className="mb-3 flex gap-1 border-b border-border" aria-label="文面の表示">
            {[
              { value: 'edit', label: '編集' },
              { value: 'rationale', label: messages.message.rationale },
            ].map((item) => (
              <Tabs.Trigger
                key={item.value}
                value={item.value}
                className={cn(tabTriggerClass, 'flex-1 justify-center')}
              >
                {item.label}
              </Tabs.Trigger>
            ))}
          </Tabs.List>
          <Tabs.Content value="edit">{editor}</Tabs.Content>
          <Tabs.Content value="rationale">{rationalePanel}</Tabs.Content>
        </Tabs.Root>
      </div>

      <BottomActions className="lg:hidden">
        <Button
          variant="outline"
          size="lg"
          onClick={openTestSend}
          aria-label={messages.message.testSend}
          title={messages.message.testSend}
        >
          <FlaskConical aria-hidden />
        </Button>
        {hasRole('manager') && message.status === 'draft' && (
          <Button
            variant="outline"
            size="lg"
            className="flex-1"
            onClick={() => approve.mutate()}
            disabled={overLimit || message.ngPhraseHits.length > 0}
          >
            {messages.message.approve}
          </Button>
        )}
        <Button
          size="lg"
          className="flex-1"
          onClick={() => setSendOpen(true)}
          disabled={isSent || overLimit}
        >
          {messages.message.send}
        </Button>
      </BottomActions>

      <ConfirmDialog
        open={sendOpen}
        onOpenChange={setSendOpen}
        title={messages.message.send}
        summary={`${message.companyName}（${message.destination?.value ?? '宛先なし'}）に${message.channel === 'email' ? 'メール' : 'フォーム'}で本番送信します。テスト送信ではありません。`}
        detail={
          <div className="space-y-2 text-body">
            <p className="font-semibold">{subject}</p>
            <p className="max-h-40 overflow-y-auto whitespace-pre-wrap rounded-control bg-muted p-3 text-foreground-muted">
              {body}
            </p>
          </div>
        }
        confirmText={messages.message.send}
        onConfirm={() => send.mutate()}
        isPending={send.isPending}
      />

      <ConfirmDialog
        open={testOpen}
        onOpenChange={setTestOpen}
        title={messages.message.testSend}
        summary={messages.message.testSendNote}
        detail={
          <div className="space-y-3">
            <Field label={messages.message.testSendTo} required>
              {(props) => (
                <Input
                  {...props}
                  type="email"
                  value={testTo}
                  onChange={(e) => setTestTo(e.target.value)}
                  placeholder="info@example.co.jp"
                />
              )}
            </Field>
            {dirty && (
              <p className="text-note text-foreground-muted">
                編集中の内容は自動保存されてから反映されます。保存済みの内容で送ります。
              </p>
            )}
          </div>
        }
        confirmText={messages.message.testSend}
        onConfirm={() => testSend.mutate()}
        isPending={testSend.isPending}
      />
    </>
  );
};
